const MESSAGE_REVIEW_REASON_LABELS: Record<string, string> = {
  recipient_mismatch: '收件号码与任务要求不一致',
  body_mismatch: '短信正文与任务要求不一致',
  green_sms_detected: '检测到普通短信（SMS），任务要求使用 iMessage',
  sent_telemetry_missing: '未检测到短信发送完成记录',
  launch_token_invalid: '本次任务启动凭证无效',
  challenge_invalid: '本次任务验证凭证无效',
  attestation_invalid: '设备验证失败',
  evidence_unreadable: '截图无法识别',
  evidence_reused: '截图与历史提交重复',
  evidence_conflict: '截图内容冲突',
  screenshot_conflict: '多张截图内容不一致',
  screenshot_too_early: '截图时间早于发送时间',
  suppressed_recipient: '收件号码被隐藏，无法核对',
  policy_blocked_content: '短信内容不符合发送规则',
  rate_limit_exceeded: '提交过于频繁，请人工核对',
  manual_review_required: '需要人工复核',
  model_output_invalid: 'AI未能完整识别截图关键信息（收件人、正文、短信状态或截图时间）',
  kill_switch_enabled: '短信任务当前已暂停',
  compliance_go_no_go_missing: '缺少合规放行确认',
};

export function messageReviewReasonLabel(code?: string | null, emptyLabel = '无异常'): string {
  if (!code) return emptyLabel;
  return MESSAGE_REVIEW_REASON_LABELS[code] ?? REVIEW_FAILURE_LABELS[code] ?? (code.startsWith('ocr_') ? 'OCR 服务异常，请查看服务日志' : '未知审核原因');
}

export function messageReviewReasonCodesLabel(
  codes?: Array<string | null> | null,
  emptyLabel = '无',
): string {
  const labels = (codes ?? [])
    .filter((code): code is string => Boolean(code))
    .map((code) => messageReviewReasonLabel(code));
  return labels.length > 0 ? labels.join('、') : emptyLabel;
}

export function messageTypeLabel(
  blueIMessageDetected?: boolean,
  reasonCodes?: string[] | null,
): string {
  if (blueIMessageDetected === true) return 'iMessage';
  if (reasonCodes?.includes('green_sms_detected')) return '普通短信（SMS）';
  return '未识别';
}

const REVIEW_FAILURE_LABELS: Record<string, string> = {
  ocr_queue_unavailable: 'OCR 队列连接失败',
  ocr_queue_full: 'OCR 队列已满',
  ocr_queue_timeout: 'OCR 排队超时',
  ocr_inference_timeout: 'OCR 识别超时',
  ocr_inference_failed: 'OCR 图片识别失败',
  ocr_worker_io_stuck: 'OCR 工作进程通信阻塞',
  ocr_worker_protocol_error: 'OCR 工作进程返回结果异常',
  ocr_worker_unkillable: 'OCR 工作进程无法重启',
  ocr_lease_check_timeout: 'OCR 工作进程续约检查超时',
  ocr_lease_lost: 'OCR 工作进程任务租约失效',
  ocr_result_too_large: 'OCR 识别结果超过大小限制',
  ocr_cancelled: 'OCR 识别已取消',
  ocr_client_disconnected: 'OCR 请求连接已断开',
  ocr_invalid_cpu_threads: 'OCR CPU 线程配置无效',
  ocr_invalid_model: 'OCR 模型配置无效',
  ocr_invalid_scenario: 'OCR 识别场景配置无效',
  ocr_request_timeout: 'OCR 请求超时',
  ocr_worker_crashed: 'OCR 工作进程异常退出',
  ocr_worker_lost: 'OCR 工作进程失去连接',
  ocr_api_busy: 'OCR 服务繁忙',
  ocr_http_error: 'OCR 服务请求失败',
  ocr_invalid_image: 'OCR 无法读取图片',
  ocr_invalid_request: 'OCR 请求参数无效',
  ocr_image_too_large: '截图超过 OCR 大小限制',
  ocr_model_load_failed: 'OCR 模型加载失败',
  ocr_fetch_failed: 'OCR 下载截图失败',
  ocr_fetch_timeout: 'OCR 下载截图超时',
  ocr_result_expired: 'OCR 识别结果已过期',
  provider_retry_exhausted: '自动审核重试次数已用尽，请恢复服务后重试审核',
  provider_config_invalid: '自动审核服务配置无效',
  provider_request_failed: '自动审核服务请求失败',
  provider_output_invalid: '自动审核服务返回结果无效',
  evidence_reader_failed: '自动审核读取截图失败',
  evidence_count_invalid: '自动审核截图数量无效',
  review_failed: '自动审核服务异常，请查看服务日志',
};

export function messageReviewFailureLabel(
  job?: { status?: string; last_error_code?: string } | null,
  summary?: Record<string, unknown> | null,
): string {
  const reason = job?.last_error_code || (typeof summary?.reason === 'string' ? summary.reason : '');
  if (!reason || reason === 'manual_review_sampled' || reason === 'manual_review_required') return '';
  const label = REVIEW_FAILURE_LABELS[reason];
  // Evidence summaries may contain business reasons; only job errors are unknown service failures.
  if (!label && !job?.last_error_code) return '';
  if (reason === 'provider_retry_exhausted') {
    const cause = summary?.failure_code || summary?.previous_failure_code;
    const causeLabel = typeof cause === 'string' ? REVIEW_FAILURE_LABELS[cause] : undefined;
    return causeLabel ? `${causeLabel}；${label}` : label;
  }
  return label || '自动审核服务异常，请查看服务日志';
}
